import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Euro, Plus, Loader2, Save, ToggleLeft, ToggleRight, ArrowUp, ArrowDown, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function CoachPricing() {
  const [offers, setOffers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [creating, setCreating] = useState({ label: "", description: "", price: "", requires_booking: false });
  const [busy, setBusy] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [savingTiers, setSavingTiers] = useState(false);

  const loadTiers = () => api.get("/admin/full-program-tiers").then((r) => setTiers(r.data.tiers || [])).catch(() => {});
  useEffect(() => { loadTiers(); }, []);

  const updTier = (i, field, val) => setTiers((t) => t.map((x, j) => (j === i ? { ...x, [field]: val } : x)));
  const addTier = () => setTiers((t) => [...t, { min: "", max: "", pct: "" }]);
  const removeTier = (i) => setTiers((t) => t.filter((_, j) => j !== i));
  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      const payload = { tiers: tiers.map((t) => ({ min: parseInt(t.min), max: parseInt(t.max), pct: parseInt(t.pct) })) };
      const r = await api.post("/admin/full-program-tiers", payload);
      setTiers(r.data.tiers); toast.success("Grille de réduction enregistrée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingTiers(false); }
  };

  const load = () => api.get("/coach/pricing").then((r) => {
    setOffers(r.data.offers || []);
    const d = {};
    (r.data.offers || []).forEach((o) => { d[o.key] = { label: o.label, description: o.description || "", price: o.price, promo_price: o.promo_price || "", features: (o.features || []).join("\n"), requires_booking: !!o.requires_booking }; });
    setDrafts(d);
  });
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const d = drafts[key];
    setBusy(true);
    try {
      await api.patch(`/coach/pricing/${key}`, { label: d.label, description: d.description, price: Number(d.price), promo_price: d.promo_price === "" ? 0 : Number(d.promo_price), features: (d.features || "").split("\n").map((s) => s.trim()).filter(Boolean), requires_booking: !!d.requires_booking });
      toast.success("Offre mise à jour · Stripe et page tarifs synchronisés");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= offers.length) return;
    const a = offers[idx], b = offers[j];
    setBusy(true);
    try {
      await Promise.all([
        api.patch(`/coach/pricing/${a.key}`, { order: b.order ?? j + 1 }),
        api.patch(`/coach/pricing/${b.key}`, { order: a.order ?? idx + 1 }),
      ]);
      load();
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const toggleActive = async (o) => {
    try { await api.patch(`/coach/pricing/${o.key}`, { active: !o.active }); toast.success(o.active ? "Désactivée" : "Activée"); load(); }
    catch { toast.error("Erreur"); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer « ${o.label} » ?`)) return;
    try { await api.delete(`/coach/pricing/${o.key}`); toast.success("Offre supprimée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const create = async () => {
    if (!creating.label || !creating.price) { toast.error("Nom et prix requis"); return; }
    setBusy(true);
    try {
      await api.post("/coach/pricing", { label: creating.label, description: creating.description, price: Number(creating.price), requires_booking: !!creating.requires_booking });
      toast.success("Prestation créée et publiée");
      setCreating({ label: "", description: "", price: "", requires_booking: false }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const upd = (key, field, val) => setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: val } }));

  return (
    <CoachLayout>
      <div className="fade-up max-w-3xl">
        <h1 className="text-2xl font-medium flex items-center gap-2"><Euro size={22} strokeWidth={1.6} /> Gestion tarifaire</h1>
        <p className="mt-1 text-sm text-obsidian/50">Modifie les prix, active/désactive les offres ou ajoute une prestation. Tout se met à jour automatiquement sur la page des tarifs et dans Stripe.</p>

        <div data-testid="fp-tiers-block" className="mt-6 border border-border rounded-xl bg-white p-4">
          <h2 className="text-sm font-semibold text-obsidian/80 flex items-center gap-2"><Euro size={15} /> Réduction progressive — programme complet</h2>
          <p className="mt-1 text-xs text-obsidian/50">Le nombre de cycles est déterminé par MAI selon l'objectif (max 12). Le total du plan complet applique la réduction du palier correspondant.</p>
          <div className="mt-3 space-y-2">
            <div className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 text-[11px] uppercase tracking-wide text-obsidian/45 px-1">
              <span>Cycles min</span><span>Cycles max</span><span>Réduction %</span><span></span>
            </div>
            {tiers.map((t, i) => (
              <div key={i} data-testid={`fp-tier-${i}`} className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 items-center">
                <input data-testid={`fp-tier-min-${i}`} type="number" value={t.min} onChange={(e) => updTier(i, "min", e.target.value)} className="border border-border rounded-sm px-2 py-1.5 text-sm tabnum" />
                <input data-testid={`fp-tier-max-${i}`} type="number" value={t.max} onChange={(e) => updTier(i, "max", e.target.value)} className="border border-border rounded-sm px-2 py-1.5 text-sm tabnum" />
                <input data-testid={`fp-tier-pct-${i}`} type="number" value={t.pct} onChange={(e) => updTier(i, "pct", e.target.value)} className="border border-border rounded-sm px-2 py-1.5 text-sm tabnum" />
                <button data-testid={`fp-tier-remove-${i}`} onClick={() => removeTier(i)} className="text-obsidian/40 hover:text-red-500 text-sm">✕</button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button data-testid="fp-tier-add" onClick={addTier} className="text-sm rounded-sm border border-border px-3 py-1.5 inline-flex items-center gap-1"><Plus size={14} /> Ajouter un palier</button>
            <button data-testid="fp-tiers-save" onClick={saveTiers} disabled={savingTiers} className="text-sm rounded-sm bg-obsidian text-white px-4 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"><Save size={14} /> {savingTiers ? "…" : "Enregistrer la grille"}</button>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {offers.map((o, idx) => (
            <div key={o.key} data-testid={`pricing-offer-${o.key}`} className={`border rounded-xl p-4 ${o.active ? "border-border bg-white" : "border-border bg-obsidian/5 opacity-70"}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wide rounded-full bg-secondary px-2 py-0.5">{o.kind === "subscription" ? "Abonnement" : "Ponctuel"}</span>
                  {o.system && <span className="text-[10px] text-obsidian/40">offre système</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`pricing-up-${o.key}`} onClick={() => move(idx, -1)} disabled={idx === 0 || busy} className="text-obsidian/40 hover:text-obsidian disabled:opacity-30"><ArrowUp size={16} /></button>
                  <button data-testid={`pricing-down-${o.key}`} onClick={() => move(idx, 1)} disabled={idx === offers.length - 1 || busy} className="text-obsidian/40 hover:text-obsidian disabled:opacity-30"><ArrowDown size={16} /></button>
                  <button data-testid={`pricing-toggle-${o.key}`} onClick={() => toggleActive(o)} className="text-obsidian/60 hover:text-obsidian flex items-center gap-1 text-sm">
                    {o.active ? <ToggleRight size={20} className="text-sage" /> : <ToggleLeft size={20} />} {o.active ? "Active" : "Inactive"}
                  </button>
                </div>
              </div>
              <div className="mt-3 grid sm:grid-cols-[1fr_1fr_120px] gap-2">
                <input data-testid={`pricing-label-${o.key}`} value={drafts[o.key]?.label || ""} onChange={(e) => upd(o.key, "label", e.target.value)} placeholder="Nom" className="border border-border rounded-sm px-2 py-1.5 text-sm" />
                <input data-testid={`pricing-desc-${o.key}`} value={drafts[o.key]?.description || ""} onChange={(e) => upd(o.key, "description", e.target.value)} placeholder="Description" className="border border-border rounded-sm px-2 py-1.5 text-sm" />
                <div className="flex items-center gap-1">
                  <input data-testid={`pricing-price-${o.key}`} type="number" step="1" value={drafts[o.key]?.price ?? ""} onChange={(e) => upd(o.key, "price", e.target.value)} className="w-20 border border-border rounded-sm px-2 py-1.5 text-sm" />
                  <span className="text-sm text-obsidian/50">€</span>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="text-xs text-obsidian/50">Prix promo (barré) :</span>
                <input data-testid={`pricing-promo-${o.key}`} type="number" step="1" placeholder="—" value={drafts[o.key]?.promo_price ?? ""} onChange={(e) => upd(o.key, "promo_price", e.target.value)} className="w-20 border border-border rounded-sm px-2 py-1 text-sm" />
                <span className="text-xs text-obsidian/40">€ · laisse vide pour retirer la promo</span>
              </div>
              {o.kind === "subscription" && (
                <div className="mt-3">
                  <label className="block text-xs text-obsidian/50 mb-1">Avantages (une puce par ligne, affichés sur la page tarifs)</label>
                  <textarea data-testid={`pricing-features-${o.key}`} rows={4} value={drafts[o.key]?.features ?? ""} onChange={(e) => upd(o.key, "features", e.target.value)}
                    placeholder={"4 semaines de programmation\nAccès illimité au chat IA\n..."} className="w-full border border-border rounded-sm px-2 py-1.5 text-sm font-mono leading-relaxed" />
                </div>
              )}
              <label className="mt-2 flex items-center gap-2 text-sm text-obsidian/70 cursor-pointer select-none">
                <input data-testid={`pricing-requires-booking-${o.key}`} type="checkbox" checked={!!drafts[o.key]?.requires_booking} onChange={(e) => upd(o.key, "requires_booking", e.target.checked)} className="w-4 h-4 accent-sage" />
                <CalendarClock size={14} className="text-obsidian/40" /> Nécessite une prise de rendez-vous (crédite un RDV et redirige le client vers l'agenda)
              </label>
              <button data-testid={`pricing-save-${o.key}`} onClick={() => save(o.key)} disabled={busy} className="mt-2 rounded-sm bg-obsidian text-white px-3 py-1.5 text-sm inline-flex items-center gap-1.5">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
              </button>
              {!o.system && (
                <button data-testid={`pricing-delete-${o.key}`} onClick={() => remove(o)} className="mt-2 ml-2 rounded-sm border border-border px-3 py-1.5 text-sm text-obsidian/60 hover:text-destructive">
                  Supprimer
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 border border-dashed border-border rounded-xl p-5" data-testid="pricing-create">
          <div className="text-sm font-medium flex items-center gap-1.5"><Plus size={15} /> Nouvelle prestation</div>
          <div className="mt-3 grid sm:grid-cols-[1fr_1fr_120px] gap-2">
            <input data-testid="new-offer-label" value={creating.label} onChange={(e) => setCreating({ ...creating, label: e.target.value })} placeholder="Nom (ex: Bilan technique)" className="border border-border rounded-sm px-2 py-1.5 text-sm" />
            <input data-testid="new-offer-desc" value={creating.description} onChange={(e) => setCreating({ ...creating, description: e.target.value })} placeholder="Description" className="border border-border rounded-sm px-2 py-1.5 text-sm" />
            <div className="flex items-center gap-1">
              <input data-testid="new-offer-price" type="number" value={creating.price} onChange={(e) => setCreating({ ...creating, price: e.target.value })} placeholder="Prix" className="w-20 border border-border rounded-sm px-2 py-1.5 text-sm" />
              <span className="text-sm text-obsidian/50">€</span>
            </div>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-obsidian/70 cursor-pointer select-none">
            <input data-testid="new-offer-requires-booking" type="checkbox" checked={!!creating.requires_booking} onChange={(e) => setCreating({ ...creating, requires_booking: e.target.checked })} className="w-4 h-4 accent-sage" />
            <CalendarClock size={14} className="text-obsidian/40" /> Nécessite une prise de rendez-vous
          </label>
          <button data-testid="new-offer-create" onClick={create} disabled={busy} className="mt-2 rounded-sm bg-sage text-white px-4 py-1.5 text-sm inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Créer et publier
          </button>
        </div>
      </div>
    </CoachLayout>
  );
}
